import React, { FC, useMemo } from 'react'
import {
  getParentDepartment,
  useLuxLabel,
  useLuxMeasure,
} from '@moonpig/web-core-utils'
import {
  DispatchCutOffQuery,
  PageTypeEnum,
  ProductModalProvider,
} from '@moonpig/web-shared-products'
import { useLoggedIn } from '@moonpig/web-core-auth'
import { useFlags } from '@moonpig/web-explore-flags'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { useSearchStore } from '../../store/SearchStore'
import { FilterSelectionError } from '../../components/FilterError/FilterErrorMessage'
import { useLuxAddExperimentData } from '../../utils'
import { Gallery } from '../../components/Gallery'
import { removeDuplicateFilters } from '../../utils/FilteringUtils/filterItemUtils'
import { CmsContent, SearchFiltersFacet, UrlFacet } from './types'

export const GalleryWrapper: FC<{
  experiments?: { [key: string]: string } | undefined
  dispatchCutOffData?: DispatchCutOffQuery
  groupCardProject?: string
  department: DepartmentsEnum[]
  urlFacets: UrlFacet[]
  metaTitle: string
  content: CmsContent
  shouldDisplaySoldOutMessage: boolean
  isInternalUser: boolean
  searchFilters: SearchFiltersFacet[]
}> = ({
  experiments,
  dispatchCutOffData,
  groupCardProject,
  department,
  urlFacets,
  metaTitle,
  content,
  shouldDisplaySoldOutMessage,
  isInternalUser,
  searchFilters,
}) => {
  const flags = useFlags()
  const { loggedIn } = useLoggedIn()
  const filtersFromStore = useSearchStore(store => store.selectedFilters)
  const results = useSearchStore(store => store.results)
  const hasFilterSelectionError = useSearchStore(
    store => store.filterSelectionError,
  )
  const clearFilterError = useSearchStore(store => store.clearFilterError)

  const combinedSearchFilters = useMemo(() => {
    const preappliedFacets = searchFilters
    const appliedFacets = filtersFromStore.map(f => ({
      facetKey: f.id,
      group: f.parent,
      userApplied: true,
    }))

    return removeDuplicateFilters([
      ...urlFacets,
      ...preappliedFacets,
      ...appliedFacets,
    ]).map(x => ({
      key: x.facetKey,
      group: x.group,
      userApplied: x.userApplied,
    }))
  }, [filtersFromStore, searchFilters, urlFacets])

  const combinedSearchFiltersForContext = useMemo(() => {
    return combinedSearchFilters.map(f => ({
      facetKey: f.key,
      group: f.group,
      userApplied: f.userApplied,
    }))
  }, [combinedSearchFilters])

  useLuxLabel('Gallery Page')
  useLuxMeasure(false)
  const luxAbTestCustomData = [
    ...Object.entries(experiments ?? {}).map(([name, value]) => ({
      name: `experiment_${name}`,
      value,
    })),
    {
      name: 'search_department',
      value: department.length
        ? getParentDepartment(department[0])
        : 'ALL_CARDS',
    },
    {
      name: 'search_product_count',
      value: results.totalCount.toString(),
    },
    {
      name: 'search_applied_filters',
      value: filtersFromStore.length.toString(),
    },
    {
      name: 'search_preapplied_filters',
      value: searchFilters.length.toString(),
    },
    {
      name: 'user_is_authenticated',
      value: loggedIn.toString(),
    },
  ]
  useLuxAddExperimentData(luxAbTestCustomData)

  return (
    <ProductModalProvider
      context={{
        location: content.title,
        pageType: PageTypeEnum.GALLERY,
        filters: combinedSearchFiltersForContext,
        totalNumberOfProducts: results.totalCount,
        flags,
      }}
    >
      {hasFilterSelectionError && (
        <FilterSelectionError onDismiss={clearFilterError} />
      )}

      <Gallery
        totalCount={results.totalCount}
        content={content}
        metaTitle={metaTitle}
        products={results.products}
        presetFacets={searchFilters}
        experiments={experiments}
        dispatchCutOffData={dispatchCutOffData}
        shouldDisplaySoldOutMessage={shouldDisplaySoldOutMessage}
        groupCardProject={groupCardProject}
        totalClickRankDocumentCount={results.totalClickRankDocumentCount}
        isInternalUser={isInternalUser}
      />
    </ProductModalProvider>
  )
}
